import React from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { CircularProgress } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'

import { BILLING_CARD, BILLING_INVOICE, PAYER_PRIMARY, PAYER_SELF, formatTermDate, planDisplay, primaryLabel } from './addOnRenewalHelpers'

/**
 * Step 2: who pays. "Add to {primary}'s subscription" only when the primary
 * authorized it (it is then in payerOptions), and then it is preselected with
 * "{primary} has opted to pay" above it; "Pay myself" always, so the member
 * can override.
 *
 * Pay myself then asks how to be billed: an emailed invoice each month (due
 * in 7 days), or the card charged automatically. The default is the card when
 * one is on file, else the invoice (set by the parent once the card check
 * returns). Card billing with no card on file continues to the card step.
 *
 * paymentMethod is { hasDefaultPaymentMethod, card } for the add-on's own
 * customer, or null while it loads.
 */
const styles = (theme) => ({
    group: {
        display: 'grid',
        gap: theme.spacing(1.25)
    },
    choice: {
        margin: 0,
        width: '100%',
        alignItems: 'flex-start',
        padding: theme.spacing(1.25, 1.75, 1.25, 0.5),
        border: `1.5px solid ${theme.palette.divider}`,
        borderRadius: 11,
        background: theme.palette.background.paper
    },
    choiceOn: {
        borderColor: theme.palette.primary.main,
        background: theme.palette.primary.light
    },
    choiceTitle: {
        display: 'block',
        fontWeight: 700,
        paddingTop: 9
    },
    choiceText: {
        display: 'block',
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
        marginTop: 2
    },
    callout: {
        padding: theme.spacing(1.5, 1.75),
        borderRadius: 10,
        background: theme.palette.background.field,
        border: `1px solid ${theme.palette.divider}`
    },
    subhead: {
        fontWeight: 600,
        margin: theme.spacing(2, 0, 1)
    }
})

// A full-width selectable card; its accessible name is just the title.
const Choice = ({ classes, value, selected, title, text }) => <FormControlLabel
    value={value}
    className={[classes.choice, selected ? classes.choiceOn : ''].join(' ')}
    control={<Radio color="primary" inputProps={{ 'aria-label': title }} />}
    label={<span><span className={classes.choiceTitle}>{title}</span>{text && <span className={classes.choiceText}>{text}</span>}</span>}
/>

const PaymentStep = ({ classes, payerOptions = [], primaryName, plan, termStart, payer, onPayerChange, paymentMethod, billing, onBillingChange, onContinue, continueLabel = 'Continue', busy }) => {
    const primaryOffered = payerOptions.indexOf(PAYER_PRIMARY) !== -1
    const name = primaryLabel(primaryName)
    const loadingCard = payer === PAYER_SELF && !paymentMethod
    const hasCard = !!(paymentMethod && paymentMethod.hasDefaultPaymentMethod)
    const card = paymentMethod && paymentMethod.card
    const planName = planDisplay(plan).name
    const priceLine = planDisplay(plan).priceLine
    const start = formatTermDate(termStart)

    return <Grid container direction="column" spacing={2}>
        <Grid item>
            <Typography variant="h6">How would you like to pay?</Typography>
        </Grid>
        {primaryOffered && <Grid item>
            <Typography data-testid="primary-opted" className={classes.callout}>
                {name} has opted to pay for your {planName} membership. You can still choose to pay for it yourself.
            </Typography>
        </Grid>}
        <Grid item>
            <FormControl component="fieldset" style={{ width: '100%' }}>
                <RadioGroup aria-label="payer" name="payer" value={payer || ''} onChange={(e) => onPayerChange(e.target.value)} className={classes.group}>
                    {primaryOffered && <Choice classes={classes} value={PAYER_PRIMARY} selected={payer === PAYER_PRIMARY}
                        title={`Add to ${name}'s subscription`} text={`${name} is billed for it; nothing for you to pay.`} />}
                    <Choice classes={classes} value={PAYER_SELF} selected={payer === PAYER_SELF}
                        title="Pay myself" text="Invoice me monthly, or pay automatically by card." />
                </RadioGroup>
            </FormControl>
        </Grid>

        {payer === PAYER_PRIMARY && <Grid item>
            <Typography data-testid="paid-by-primary">
                Your {planName} membership will be added to {name}'s monthly subscription{start ? ` from ${start}` : ''}. {name} is billed for it, so there's nothing for you to pay and no card is needed.
            </Typography>
        </Grid>}

        {payer === PAYER_SELF && <Grid item>
            {loadingCard && <span>Checking for a card on file <CircularProgress size={'14px'} /></span>}
            {paymentMethod && <>
                <Typography className={classes.subhead}>How would you like to be billed?</Typography>
                <FormControl component="fieldset" style={{ width: '100%' }}>
                    <RadioGroup aria-label="billing" name="billing" value={billing || ''} onChange={(e) => onBillingChange(e.target.value)} className={classes.group}>
                        <Choice classes={classes} value={BILLING_INVOICE} selected={billing === BILLING_INVOICE}
                            title="Invoice me monthly" text="Emailed each month, due within 7 days. No card needed." />
                        <Choice classes={classes} value={BILLING_CARD} selected={billing === BILLING_CARD}
                            title="Pay automatically by card" text="Charged to your card each month." />
                    </RadioGroup>
                </FormControl>
                {billing === BILLING_INVOICE && <Typography data-testid="invoice-billing" style={{ marginTop: 10 }}>
                    Each month we'll email you an invoice for {priceLine}, due within 7 days. You can pay it online by card. No card is needed now - you can add one later.
                </Typography>}
                {billing === BILLING_CARD && hasCard && <Typography data-testid="card-on-file" style={{ marginTop: 10 }}>
                    Card on file will be charged each month{card && card.last4 ? ` (${card.brand ? card.brand.toUpperCase() + ' ' : ''}ending ${card.last4})` : ''}.
                </Typography>}
                {billing === BILLING_CARD && !hasCard && <Typography data-testid="no-card-on-file" style={{ marginTop: 10 }}>
                    You don't have a card on file. You'll add one next.
                </Typography>}
            </>}
        </Grid>}

        <Grid item>
            <Button fullWidth variant="contained" size="large" color="primary"
                disabled={busy || !payer || loadingCard || (payer === PAYER_SELF && !billing)}
                onClick={onContinue}>
                {busy ? <span>Please wait <CircularProgress size={'14px'} /></span> : continueLabel}
            </Button>
        </Grid>
    </Grid>
}

export default withStyles(styles)(PaymentStep)
